import React from 'react';
import { RefreshCw, Check, Calendar } from 'lucide-react';
import { theme } from '../../theme';

const Header = ({ 
  viewMode, 
  selectedProject, 
  setSelectedProject, 
  projectsToUse, 
  fetchData, 
  isLoading, 
  overallProgress 
}) => {
  // Sort projects by due date (earliest first)
  const sortedProjects = [...projectsToUse].sort((a, b) => {
    // Convert dates to timestamps for comparison
    return new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime();
  });

  return (
    <div className="bg-white border-b border-gray-100 transition-all duration-300 sticky top-0 z-10 shadow-sm">
      <div className="max-w-7xl mx-auto">
        {/* Brand and Title row */}
        <div className="p-4 md:p-6 flex flex-col md:flex-row justify-between items-start md:items-center">
          <h1 className="font-light transition-all duration-300 text-2xl md:text-3xl text-gray-800 flex items-center">
            <span className="font-semibold text-green-600 mr-1">Vasant</span> Studio
            <span className="ml-3 text-sm font-normal bg-gray-100 text-gray-500 rounded-full px-3 py-1">Spring 2025</span>
          </h1>
          
          <div className="mt-4 md:mt-0 flex flex-col items-start md:items-end">
            <div className="flex items-center mb-2">
              <span className="mr-2 text-gray-500 text-sm">Completion</span>
              <span className="font-semibold text-xl text-gray-800">{overallProgress}%</span>
            </div>
            <div className="w-full md:w-64 bg-gray-100 rounded-full h-2 overflow-hidden">
              <div 
                className="h-2 rounded-full transition-all duration-1000 ease-out"
                style={{ 
                  width: `${overallProgress}%`, 
                  backgroundColor: overallProgress >= 80 ? theme.colors.status.success : 
                                  overallProgress >= 40 ? theme.colors.secondary.accent : 
                                  theme.colors.primary.main 
                }}
              ></div>
            </div>
          </div>
        </div>
        
        {/* Project Tabs - Now sorted by due date */}
        <div className="flex items-center border-t border-gray-100">
          <div className="flex-1 overflow-x-auto hide-scrollbar">
            <div className="flex">
              {sortedProjects.map(project => {
                // Format due date to show in tab tooltip
                const formattedDate = new Date(project.dueDate).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'short', 
                  day: 'numeric'
                });
                
                return (
                  <button
                    key={project.name}
                    className={`whitespace-nowrap py-3 px-5 md:px-6 border-b-2 font-medium text-sm transition-all duration-200 flex items-center
                      ${selectedProject === project.name 
                        ? 'border-green-600 text-green-700 bg-green-50' 
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}
                    `}
                    onClick={() => setSelectedProject(project.name)}
                    title={`Due: ${formattedDate}`}
                  >
                    {selectedProject === project.name ? (
                      <Check size={14} className="mr-1.5 text-green-600" />
                    ) : (
                      <Calendar size={14} className="mr-1.5 opacity-70" />
                    )}
                    {project.name}
                  </button>
                );
              })}
            </div>
          </div>
          
          <button 
            className="bg-white p-3 border-l border-gray-100 hover:bg-gray-50 transition-all duration-200 flex items-center"
            onClick={fetchData}
            disabled={isLoading}
            aria-label="Refresh data"
          >
            <RefreshCw size={16} className={`text-gray-600 ${isLoading ? 'animate-spin text-green-500' : ''}`} />
            {viewMode !== 'mobile' && <span className="ml-1.5 mr-1 text-gray-600">Refresh</span>}
          </button>
        </div>
      </div>

      {/* Add custom scrollbar styling */}
      <style jsx="true">{`
        .hide-scrollbar {
          -ms-overflow-style: none;  /* Internet Explorer and Edge */
          scrollbar-width: none;  /* Firefox */
        }
        
        .hide-scrollbar::-webkit-scrollbar {
          display: none; /* Chrome, Safari, Opera */
        }
      `}</style>
    </div>
  );
};

export default Header;
